import axios from 'axios';
import type { FeedbackReason } from '@/components/home/insights/feedback/reasons';
import type { AdpDocument } from '@/types/document';
import type {
  InsightAdjustmentRegenerateTrackingData,
  InsightFeedbackEvaluation,
  InsightHypothesisReason,
  InsightRecommendedActionAgentType,
  InsightRecommendedActionPrefill,
  InsightRecommendedActionPrefillInputMap,
  InsightSavedHint,
  InsightSavedHintDetail,
  InsightTopic,
} from '@/types/insights';

export const generateInsights = async (): Promise<void> => {
  await axios.post('/insights');
};

export const regenerateInsights = async (
  trackingData?: InsightAdjustmentRegenerateTrackingData,
): Promise<{ uuid: string }> => {
  const { data } = await axios.post<{ uuid: string }>(
    '/insights/regenerate',
    trackingData ? { tracking_data: trackingData } : undefined,
  );
  return data;
};

export const createInsightFeedback = async (params: {
  hint_uuid: string;
  evaluation: InsightFeedbackEvaluation;
  reasons: FeedbackReason[];
  comment: string;
}): Promise<void> => {
  await axios.post('/insights/feedbacks', {
    hint_uuid: params.hint_uuid,
    evaluation: params.evaluation,
    reasons: params.reasons,
    comment: params.comment,
  });
};

type InsightRecommendedActionPrefillResponse = {
  [K in InsightRecommendedActionAgentType]: {
    agent_type: K;
    input?: InsightRecommendedActionPrefillInputMap[K] | null;
  };
}[InsightRecommendedActionAgentType];

const promptTextInput = (
  promptText: string | null | undefined,
): { prompt_text: string } | null =>
  typeof promptText === 'string' && promptText !== ''
    ? { prompt_text: promptText }
    : null;

export const toInsightRecommendedActionPrefill = (
  response: InsightRecommendedActionPrefillResponse,
): InsightRecommendedActionPrefill => {
  switch (response.agent_type) {
    case 'tech_exploration':
    case 'survey':
      return {
        agent_type: response.agent_type,
        input: promptTextInput(response.input?.prompt_text),
      };
    case 'forced_ideation':
      return { agent_type: 'forced_ideation', input: response.input ?? null };
    case 'market_sizing':
      return { agent_type: 'market_sizing', input: response.input ?? null };
    default: {
      const exhaustive: never = response;
      return exhaustive;
    }
  }
};

export const getInsightRecommendedActionPrefill = async (
  uuid: string,
): Promise<InsightRecommendedActionPrefill> => {
  const { data } = await axios.get<InsightRecommendedActionPrefillResponse>(
    `/insights/recommended_actions/${uuid}`,
  );
  return toInsightRecommendedActionPrefill(data);
};

type InsightSavedHintDetailResponse = InsightSavedHint & {
  hint_id: number;
  focus_point_reason: string;
  hypothesis_reasons: InsightHypothesisReason[];
  topic: InsightTopic | null;
  topics: InsightTopic[];
  sources: AdpDocument[];
};

export const toInsightSavedHintDetail = (
  response: InsightSavedHintDetailResponse,
): InsightSavedHintDetail => ({
  created_at: response.created_at,
  insight_id: response.insight_id,
  insight_date: response.insight_date,
  axis_id: response.axis_id,
  axis: response.axis,
  position: response.position,
  hint: {
    id: response.hint_id,
    uuid: response.hint_uuid,
    focus_point: response.focus_point,
    focus_point_reason: response.focus_point_reason,
    hypothesis: response.hypothesis,
    hypothesis_reasons: response.hypothesis_reasons,
    recommended_actions: response.recommended_actions,
    saved: true,
    topic: response.topic,
  },
  topic: response.topic,
  topics: response.topics,
  sources: response.sources,
});

export type InsightSavedHintDetailsPage = {
  savedHints: InsightSavedHintDetail[];
  hasNextPage: boolean;
};

type InsightSavedHintDetailsResponse = {
  saved_hints: InsightSavedHintDetailResponse[];
  has_next_page: boolean;
};

export const getInsightSavedHintDetails = async (
  page: number,
  limit: number,
): Promise<InsightSavedHintDetailsPage> => {
  const { data } = await axios.get<InsightSavedHintDetailsResponse>(
    '/insights/saved_hints',
    { params: { page, limit, with_details: true } },
  );
  return {
    savedHints: data.saved_hints.map(toInsightSavedHintDetail),
    hasNextPage: data.has_next_page,
  };
};

export const createInsightSavedHint = async (
  hintUuid: string,
): Promise<void> => {
  await axios.post('/insights/saved_hints', { hint_uuid: hintUuid });
};

export const deleteInsightSavedHint = async (
  hintUuid: string,
): Promise<void> => {
  await axios.delete(`/insights/saved_hints/${hintUuid}`);
};
